import React, { useState, useRef, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const RouteInfoCard = ({ distance, duration, steps }) => {
  const [hidden, setHidden] = useState(false);
  const cardRef = useRef(null);
  const [cardCenterY, setCardCenterY] = useState(0);

  // Get the vertical center of the card to position the "Show" button
  useLayoutEffect(() => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setCardCenterY(rect.top + rect.height / 2);
    }
    // We run this effect when the card's content changes, as that might change its height.
  }, [distance, duration, steps]);

  const cardVariants = {
    visible: { opacity: 1, x: 0 },
    hidden: { opacity: 0, x: "100%" },
  };

  const buttonVariants = {
    visible: { opacity: 1, x: 0 },
    hidden: { opacity: 0, x: "100%" },
  };

  return (
    <>
      <AnimatePresence>
        {hidden && (
          <motion.button
            key="show-button"
            className="fixed z-[2002] hover:bg-amber-700/90 text-amber-300 hover:text-white rounded-l-xl p-2 -translate-y-1/2"
            style={{
              top: cardCenterY ? `calc(${cardCenterY}px - 1.3rem)` : "50%", // Adjust top position with rem
              right: "-0.6rem", // Move button to the right
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              background: "transparent",
            }}
            aria-label="顯示路線資訊"
            onClick={() => setHidden(false)}
            variants={buttonVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!hidden && (
          <motion.div
            key="info-card"
            ref={cardRef}
            className="fixed right-5 top-16 z-[2001] bg-[#181c23]/95 backdrop-blur-lg rounded-xl shadow-2xl px-5 py-4 min-w-[140px] border border-[#23272f] flex flex-col gap-3 items-stretch text-gray-100"
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {/* Hide Button */}
            <button
              className="absolute -right-8 top-1/2 -translate-y-1/2 z-10 bg-transparent hover:bg-amber-700/90 text-amber-300 hover:text-white rounded-r-xl p-2 transition"
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              aria-label="隱藏路線資訊"
              onClick={() => setHidden(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-7 w-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
            <div className="font-bold text-lg text-amber-300 pb-2 border-b border-[#23272f] text-center tracking-wide flex items-center gap-2">
              <svg
                className="w-5 h-5 text-amber-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 2l2.09 6.26L20 9.27l-5 3.64L16.18 20 12 16.77 7.82 20 9 12.91l-5-3.64 5.91-.91z"
                />
              </svg>
              路線資訊
            </div>
            <div className="flex items-center justify-between text-base">
              <span className="text-lg text-amber-400">📏</span>
              <span className="font-medium">{distance}</span>
            </div>
            <div className="flex items-center justify-between text-base">
              <span className="text-lg text-amber-400">⏱️</span>
              <span className="font-medium">{duration}</span>
            </div>
            <div className="flex items-center justify-between text-base">
              <span className="text-lg text-amber-400">👣</span>
              <span className="font-medium">{steps} 步</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RouteInfoCard;
